<template>
  <BaseBody>
    <BaseTopBar>
      <BaseTopBarContent className="left">
        {{ $t('sidebar.configuration') }} > {{ $t('sidebar.assignlotserial') }}
      </BaseTopBarContent>
    </BaseTopBar>
    <BaseContent :loading="loading">
      <Section v-if="appliedInventorySkuIds.length === 0">
        <BaseNotFoundData
          :title="$t('apps.inventory.message.notfoundassignedskus')"
          :detail="$t('apps.inventory.message.assignskuat')"
        />
      </Section>

      <template v-else>
        <Section>
          <SectionHeader :title="$t('apps.inventory.message.assignlot')"/>
          <FormRow>
            <Col className="col-lg-12">
            <BaseNotFoundData
              v-if="!products.length"
              :title="$t('apps.inventory.message.notfoundskus')"
              :detail="$t('apps.inventory.message.createnewproducts')"
            />
            <BaseAssignSkus
              v-else
              v-model="form.assignLots"
              :items="assignLots"
            />
            </Col>
          </FormRow>
        </Section>

        <Section>
          <SectionHeader :title="$t('apps.inventory.message.assignserial')"/>
          <FormRow>
            <Col className="col-lg-12">
            <BaseNotFoundData
              v-if="!products.length"
              :title="$t('apps.inventory.message.notfoundskus')"
              :detail="$t('apps.inventory.message.createnewproducts')"
            />
            <BaseAssignSkus
              v-else
              v-model="form.assignSerials"
              :items="assignSerials"
            />
            </Col>
          </FormRow>
        </Section>

        <BaseFormAction
          v-if="products.length"
          :textCancel="$t('button.cancel')"
          :textSubmit="$t('button.save')"
          :isSubmitted="isSubmitted"
          @onCancel="handleCancel"
          @onSubmit="handleSubmit"
        />
      </template>

      <BaseConfirmDiscardModal
        :show="confirmDiscardModal.show"
        @onCancel="handleDiscardCancleModal"
        @onConfirm="handleDiscardConfirmModal"
      />
    </BaseContent>
  </BaseBody>
</template>

<script>
import { mapGetters } from 'vuex'
import { cloneDeep } from 'lodash'
import sidebarModule from './../../sidebarModule'
import submenuModule from '../submenuModule'
import discardFormMixin from '@/mixins/discardForm'

export default {
  mixins: [discardFormMixin],

  data () {
    return {
      loading: true,
      isSubmitted: false,
      form: {
        assignLots: [],
        assignSerials: [],
      },
      formOriginal: null,
    }
  },

  computed: {
    ...mapGetters({
      warehouses: 'getWarehouses',
      sidebar: 'getSidebar',
      products: 'getProducts',
      getUsedLotSerialSkus: 'getUsedLotSerialSkus',
    }),
    appliedInventorySkuIds () {
      return this.warehouses.reduce((acc, cur) => {
        const assignSkus = cur.assign_skus || []
        const skuIds = assignSkus.map((sku) => sku.sku_id)
        if (skuIds.length) {
          acc.push(...skuIds)
        }
        return acc
      }, [])
    },
    usedLotSkuIds () {
      return [...this.getUsedLotSerialSkus.usedLotSkuIds]
    },
    usedSerialSkuIds () {
      return [...this.getUsedLotSerialSkus.usedSerialSkuIds]
    },
    assignLots () {
      return this.products
        .reduce((acc, cur) => {
          const skus = cur.prices
            .filter((price) => {
              const isUsedSerial = this.usedSerialSkuIds.some((skuId) => price.sku_id === skuId)
              const isAssignedInventory = this.appliedInventorySkuIds.includes(price.sku_id)
              return !isUsedSerial && isAssignedInventory
            })
            .map((price) => {
              const isUsedLot = this.usedLotSkuIds.some((skuId) => price.sku_id === skuId)
              return {
                text: this.getProductFullName(cur.product_name, price.attributes),
                value: price.sku_id,
                disabled: isUsedLot,
              }
            })
          if (skus.length) {
            acc.push(...skus)
          }
          return acc
        }, [])
    },
    selectedLotSkuIds () {
      return this.form.assignLots.reduce((acc, cur) => {
        return acc.push(cur.value) && acc
      }, [])
    },
    assignSerials () {
      return this.products
        .reduce((acc, cur) => {
          const skus = cur.prices
            .filter((price) => {
              const isUsedLot = this.usedLotSkuIds.some((skuId) => price.sku_id === skuId)
              const isAssignedInventory = this.appliedInventorySkuIds.includes(price.sku_id)
              return !isUsedLot && isAssignedInventory
            })
            .map((price) => {
              const isUsedSerial = this.usedSerialSkuIds.some((skuId) => price.sku_id === skuId)
              return {
                text: this.getProductFullName(cur.product_name, price.attributes),
                value: price.sku_id,
                disabled: isUsedSerial,
              }
            })
          if (skus.length) {
            acc.push(...skus)
          }
          return acc
        }, [])
    },
    selectedSerialSkuIds () {
      return this.form.assignSerials.reduce((acc, cur) => {
        return acc.push(cur.value) && acc
      }, [])
    },
  },

  watch: {
    warehouses () {
      sidebarModule.init(cloneDeep(require('../sidebar.json')))
      sidebarModule.update(this.sidebar, 1, submenuModule.getWarehouses(this.warehouses))
      sidebarModule.active({
        sidebarAtIdx: 9,
        submenuAtIdx: 2,
      })
    },
    selectedLotSkuIds (newIds) {
      const removeValues = this.form.assignSerials.reduce((acc, cur) => {
        if (newIds.includes(cur.value)) {
          acc.push(cur.value)
        }
        return acc
      }, [])
      if (removeValues.length > 0) {
        for (const value of removeValues) {
          const findIndex = this.form.assignSerials.findIndex((item) => item.value === value)
          this.form.assignSerials.splice(findIndex, 1)
        }
      }
    },
    selectedSerialSkuIds (newIds) {
      const removeValues = this.form.assignLots.reduce((acc, cur) => {
        if (newIds.includes(cur.value)) {
          acc.push(cur.value)
        }
        return acc
      }, [])
      if (removeValues.length > 0) {
        for (const value of removeValues) {
          const findIndex = this.form.assignLots.findIndex((item) => item.value === value)
          this.form.assignLots.splice(findIndex, 1)
        }
      }
    },
  },

  async created () {
    await this.fetchData()
    this.setState()
    this.formOriginal = cloneDeep(this.form)
  },

  beforeMount () {
    // init page
    this.initPage({
      section: 'app',
      name: this.$t('app.inventory.title'),
      current: {
        app_name: 'inventory',
        app_id: 3,
        module_id: 10,
      },
    })
    // set sidebar stockcontrols ui
    sidebarModule.init(this.getObject(require('../sidebar.json')))
    sidebarModule.update(this.sidebar, 1, submenuModule.getWarehouses(this.warehouses))
    sidebarModule.active({
      sidebarAtIdx: 9,
      submenuAtIdx: 2,
    })
  },

  destroyed () {
    this.leavePage(this.$Progress, [])
  },

  methods: {
    async fetchData () {
      this.loading = true
      await Promise.all([
        this.$store.dispatch('getWarehouses'),
        this.$store.dispatch('getProducts'),
        this.$store.dispatch('getUsedLotSerialSkus'),
      ])
      this.loading = false
      this.showFinish(this.$Progress)
    },
    setState () {
      for (const product of this.products) {
        for (const sku of product.prices) {
          if (sku.config) {
            if (sku.config.inventory.use_lot) {
              this.form.assignLots.push({
                text: this.getProductFullName(product.product_name, sku.attributes),
                value: sku.sku_id,
                active: true,
              })
            }
            if (sku.config.inventory.use_serial) {
              this.form.assignSerials.push({
                text: this.getProductFullName(product.product_name, sku.attributes),
                value: sku.sku_id,
                active: true,
              })
            }
          }
        }
      }
    },
    handleCancel () {
      this.form = cloneDeep(this.formOriginal)
    },
    async handleSubmit () {
      this.isSubmitted = true
      const dataSet = {
        'use_lot_sku_ids': this.selectedLotSkuIds.map((value) => value),
        'use_serial_sku_ids': this.selectedSerialSkuIds.map((value) => value),
      }
      const response = await this.$store.dispatch('assignLotSerial', dataSet)
      if (response.success) {
        this.isSubmitted = false
        this.notify(this.$vs, {
          text: this.$t('message.saved'),
          color: 'success',
          icon: 'cloud',
        })
        this.fetchData()
      }
    },
  },
}
</script>



// WEBPACK FOOTER //
// src/components/apps/inventory/configuration/AssignLotSerial.vue